/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { delayedResponse, makeMockResponse } from '../mockUtils';
import {
    DocumentsStatDto,
    PaymentTypesStatDto,
    TurnoversByActivitiesDto,
    OperatorCashBoxesStatDto,
    RegistryItemsCountDto,
    DocsCountsStat,
    DevicesCountsByStatuses,
    CashBoxesStatDto,
} from './dashBoardClient.typings';
import { operatorsCashBoxesStat, registryByTaxationType } from './dashBoardFactories';
import { factoryTBuilder, fields } from 'factory-t/build';
import { FieldFactoryContext } from 'factory-t/build/common';
import { StoreCategory, StoreType } from '../stores/storesClientTypings';
import { TURNOVERS_FIRST_CATEGORY, TURNOVERS_SECOND_CATEGORY } from '../../reducers/dashBoardFinReducer';

const DOCUMENT_TYPES_STAT_URL = /\/infographics\/documentTypesStat/;
const PAYMENT_TYPES_STAT_URL = /\/infographics\/paymentTypesStat/;
const TURNOVERS_BY_ACTIVITY_URL = /\/infographics\/turnoverByActivity/;
const CASHBOXES_STAT_URL = /\/infographics\/cashBoxesStat/;
const OPERATORS_CASHBOXES_STAT_URL = /\/infographics\/operatorsCashBoxesStat/;
const COVERAGE_URL = /\/infographics\/coverageByTaxationTypes/;
const DOCUMENT_COUNTER_URL = /\/infographics\/documentsCounter/;
const DEVICE_STATUSES_STAT_URL = /\/infographics\/deviceStatusesStat/;

export function addDashBoardMockApi(mockAdapter: MockAdapter): void {
    mockAdapter.onGet(PAYMENT_TYPES_STAT_URL).reply((config) => {
        const paymentTypesStat: PaymentTypesStatDto[] = [];

        let cashSum = 1000;
        let cashlessSum = 2000;
        let creditSum = 3000;
        let bonusSum = 4000;
        let prepaymentSum = 5000;
        let sum = 1000;

        for (let i = 15; i < 500; i++) {
            cashSum += Math.round((Math.random() < 0.5 ? 1 : -0.5) * Math.random() * 100);
            cashlessSum += Math.round((Math.random() < 0.5 ? 1 : -0.5) * Math.random() * 100);
            creditSum += Math.round((Math.random() < 0.5 ? 1 : -0.5) * Math.random() * 100);
            bonusSum += Math.round((Math.random() < 0.5 ? 1 : -0.5) * Math.random() * 100);
            prepaymentSum += Math.round((Math.random() < 0.5 ? 1 : -0.5) * Math.random() * 100);

            if (cashSum < 100) {
                cashSum = 100;
            }

            if (cashlessSum < 100) {
                cashlessSum = 100;
            }

            if (creditSum < 100) {
                creditSum = 100;
            }

            if (bonusSum < 100) {
                bonusSum = 100;
            }

            if (prepaymentSum < 100) {
                prepaymentSum = 100;
            }

            sum += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 500);

            if (sum < 0) {
                sum *= -1;
            }

            paymentTypesStat.push({
                date: new Date(2020, 0, i),
                cashSum,
                cashlessSum,
                creditSum,
                bonusSum,
                prepaymentSum,
                sum,
            });
        }

        return delayedResponse(makeMockResponse(200, paymentTypesStat, config), 2000);
    });

    mockAdapter.onGet(DOCUMENT_TYPES_STAT_URL).reply((config) => {
        const docsStat: DocumentsStatDto[] = [];

        let sale = 15000;
        let correction = 16000000;
        let moneyBack = 13900000;
        let deposit = 94000000;
        let rollback = 36000000;
        let withdraw = 28000000;
        let prepay = 1000;
        let creditpay = 2000;
        let sum = 1000;

        for (let i = 1; i < 360; i++) {
            sale += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 1300);
            correction += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 2500000);
            moneyBack += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 3300000);
            deposit += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 3900000);
            rollback += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 10000);
            withdraw += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 4800000);
            prepay += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 400000);
            creditpay += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 440000);

            if (sale < 100) {
                sale = 100;
            }

            if (correction < 100) {
                correction = 100;
            }

            if (moneyBack < 100) {
                moneyBack = 100;
            }

            if (deposit < 100) {
                deposit = 100;
            }

            if (rollback < 100) {
                rollback = 100;
            }

            if (withdraw < 100) {
                withdraw = 100;
            }

            if (prepay < 100) {
                prepay = 100;
            }

            if (creditpay < 100) {
                creditpay = 100;
            }

            sum += Math.round((Math.random() < 0.5 ? 1 : -1) * Math.random() * 400);

            if (sum < 0) {
                sum *= -1;
            }

            correction = 0.73;

            docsStat.push({
                date: new Date(2019, 0, i),
                sale,
                correction,
                moneyback: moneyBack,
                deposit,
                rollback,
                withdraw,
                prepay,
                creditpay,
                sum,
            });
        }

        return delayedResponse(makeMockResponse(200, docsStat, config), Math.random() * 100);
    });

    mockAdapter.onGet(OPERATORS_CASHBOXES_STAT_URL).reply((config) => {
        const data: OperatorCashBoxesStatDto[] = operatorsCashBoxesStat.list({ count: 40 });

        operatorsCashBoxesStat.resetCount();
        data[0].name = 'Operator1 short name';

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASHBOXES_STAT_URL).reply((config) => {
        const data: CashBoxesStatDto = {
            active: 950,
            blocked: 500,
            inactive: 220,
            notBlocked: 300,
            offline: 700,
            online: 1100,
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(DEVICE_STATUSES_STAT_URL).reply((config) => {
        const data: DevicesCountsByStatuses = {
            ready: 100,
            broken: 100,
            produced: 100,
            recycled: 100,
            stock: 100,
            store: 100,
            service: 100,
            fixed: 100,
            lost: 100,
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(COVERAGE_URL).reply((config) => {
        const data: RegistryItemsCountDto = {
            companies: {
                taxationTypes: registryByTaxationType.list({ count: 5 }),
                count: 100,
            },
            stores: {
                taxationTypes: registryByTaxationType.list({ count: 5 }),
                count: 200,
            },
            cashBoxes: {
                taxationTypes: registryByTaxationType.list({ count: 5 }),
                count: 300,
            },
        };

        return delayedResponse(makeMockResponse(200, data, config), 2000);
    });

    mockAdapter.onGet(DOCUMENT_COUNTER_URL).reply((config) => {
        const data: DocsCountsStat = {
            min: 501000,
            avg: 730000,
            today: 315000,
            yesterday: 810000,
            max: 1300000,
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(TURNOVERS_BY_ACTIVITY_URL).reply((config) => {
        const { limit, categories, excludeCategories } = config.params;
        const storeTypesList = Object.values(StoreType);
        const categoriesList = Object.values(StoreCategory);

        if (excludeCategories)
            excludeCategories.forEach((item: StoreCategory) => categoriesList.splice(categoriesList.indexOf(item), 1));

        const turnoversByActivityStat = factoryTBuilder<TurnoversByActivitiesDto>({
            storeType: fields.string() as unknown as StoreType,
            category: fields.sequence(categories ? categories : categoriesList),
            turnover: fields.index(),
        })
            .setFieldFactory('turnover', () => Math.round(Math.random() * 1000))
            .setFieldFactory('storeType', (ctx: FieldFactoryContext<TurnoversByActivitiesDto>) => {
                if (categories && categories[0] === TURNOVERS_FIRST_CATEGORY) {
                    return storeTypesList[ctx.index];
                }

                if (categories && categories[0] === TURNOVERS_SECOND_CATEGORY) {
                    return storeTypesList[ctx.index + limit];
                }

                return storeTypesList[ctx.index + limit * 2];
            })
            .factory();

        const data: TurnoversByActivitiesDto[] = turnoversByActivityStat.list({ count: limit });

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });
}
